/**
 * Converting ink between canvas pixels and staff-relative units.
 *
 * Strokes are captured in canvas pixels, which stop meaning anything the moment
 * the iPad rotates or the staff is laid out at a different size. Persisted ink is
 * therefore measured against the staff itself: x in staff-spaces right of the
 * writing origin, y in staff-spaces above the bottom line.
 *
 * The conversion is exact and reversible, so a note written on the middle line at
 * one size is on the middle line at every other size. That is the difference
 * between reopening your work and reopening a picture of it.
 */

import type { Stroke } from '../ink/types';
import type { StaffLayout } from '../theory/staff';
import type { RelativeStroke } from './types';

export function toRelative(stroke: Stroke, layout: StaffLayout): RelativeStroke {
  const gap = layout.lineGap || 1;
  return {
    id: stroke.id,
    color: stroke.color,
    width: round(stroke.width / gap, 4),
    tool: stroke.tool,
    fromPen: stroke.fromPen,
    points: stroke.points.map((p) => ({
      x: round((p.x - layout.writingX) / gap, 4),
      // Up the staff is positive, which matches staff positions rather than screens.
      y: round((layout.bottomLineY - p.y) / gap, 4),
      pressure: round(p.pressure, 3),
      tiltX: Math.round(p.tiltX),
      tiltY: Math.round(p.tiltY),
      t: Math.round(p.t),
    })),
  };
}

export function fromRelative(stroke: RelativeStroke, layout: StaffLayout): Stroke {
  const gap = layout.lineGap || 1;
  return {
    id: stroke.id,
    color: stroke.color,
    width: stroke.width * gap,
    tool: stroke.tool,
    fromPen: stroke.fromPen,
    points: stroke.points.map((p) => ({
      x: p.x * gap + layout.writingX,
      y: layout.bottomLineY - p.y * gap,
      pressure: p.pressure,
      tiltX: p.tiltX,
      tiltY: p.tiltY,
      t: p.t,
    })),
  };
}

export function toRelativeAll(strokes: Stroke[], layout: StaffLayout): RelativeStroke[] {
  return strokes.map((s) => toRelative(s, layout));
}

export function fromRelativeAll(strokes: RelativeStroke[], layout: StaffLayout): Stroke[] {
  return strokes.map((s) => fromRelative(s, layout));
}

function round(value: number, places: number): number {
  const factor = 10 ** places;
  return Math.round(value * factor) / factor;
}
